import { useEffect, useRef, useState } from 'react';

/**
 * Re-fetches an entity each time `open` flips false → true so a modal
 * always renders the freshest server-side state without requiring a
 * page refresh. The `fallback` (typically the prop value passed by the
 * parent) is returned immediately so the form mounts without a spinner;
 * it is replaced silently when the fetch resolves.
 *
 * Race-guarded — if the modal closes mid-fetch, the result is dropped.
 *
 * @param {Object}   args
 * @param {boolean}  args.open        modal open state
 * @param {Function} args.fetcher     () => Promise<Entity>
 * @param {Object}   args.fallback    prop entity used until fresh data arrives
 * @param {Function} [args.onFresh]   (entity) => void — let the parent reconcile
 *                                    its cached state with the freshly-fetched entity
 * @param {boolean}  [args.enabled]   escape hatch (default true)
 * @param {*}        [args.refetchKey] When this value changes while `open`
 *                                    is true, the hook re-runs the fetcher.
 *                                    Useful for socket-driven invalidation
 *                                    (parent bumps a counter on each
 *                                    `entity:changed` signal).
 *
 * @returns {{
 *   entity: Object,
 *   isFetching: boolean,
 *   error: Error | null,
 *   refetch: () => void
 * }}
 */
export function useFreshEntity({
  open,
  fetcher,
  fallback,
  onFresh,
  enabled = true,
  refetchKey
}) {
  const [entity, setEntity] = useState(fallback);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(null);
  const inFlightRef = useRef(0);
  const wasOpenRef = useRef(false);

  // Keep `entity` in sync whenever the prop fallback changes — including
  // when the parent simultaneously flips `open` and swaps `fallback` for a
  // different record. Without this, switching from one genotype/member to
  // another while the modal is reopened in the same tick leaves `entity`
  // pointing at the previous record. The fetcher, if supplied, overlays
  // fresh data when it resolves.
  useEffect(() => {
    setEntity(fallback);
  }, [fallback]);

  const runFetch = () => {
    if (!enabled || typeof fetcher !== 'function') return;
    const ticket = ++inFlightRef.current;
    setIsFetching(true);
    setError(null);
    Promise.resolve()
      .then(() => fetcher())
      .then((fresh) => {
        if (ticket !== inFlightRef.current) return;
        if (fresh) {
          setEntity(fresh);
          if (typeof onFresh === 'function') onFresh(fresh);
        }
      })
      .catch((err) => {
        if (ticket !== inFlightRef.current) return;
        setError(err);
        // Fail open — keep showing the fallback.
        // eslint-disable-next-line no-console
        console.warn('useFreshEntity: fetch failed', err);
      })
      .finally(() => {
        if (ticket !== inFlightRef.current) return;
        setIsFetching(false);
      });
  };

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      runFetch();
    }
    if (!open) {
      // Cancel any in-flight result by bumping the ticket counter.
      inFlightRef.current += 1;
      setIsFetching(false);
    }
    wasOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Re-fetch when the parent signals an external invalidation (e.g.
  // a socket `entity:changed` event arrived). Only fires while open so
  // closed-modal subscribers don't thrash the network.
  useEffect(() => {
    if (!open) return;
    if (refetchKey === undefined) return;
    runFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchKey]);

  return { entity, isFetching, error, refetch: runFetch };
}
